import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  GraduationCap, Baby, Gamepad2, Eye, BrainCircuit, Flame, ScrollText, Infinity as InfinityIcon, MessageCircle, Share2, Heart,
  Briefcase, Hammer, Landmark, Stethoscope, Users, Handshake, History,
  Crosshair, FlaskConical, Swords, Radio, Microscope, Dices, ShieldCheck, Lock,
} from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CinematicVideo } from "@/components/CinematicVideo";
import { api } from "@/lib/api";
import { getVoterId } from "@/lib/voter";
import { CocoonMark } from "@/components/CocoonMark";

const ICONS = {
  "ia-ecole": GraduationCap,
  "ia-enfants": Baby,
  "ludification": Gamepad2,
  "attention": Eye,
  "par-coeur": ScrollText,
  "curiosite": Flame,
  "diplome": ScrollText,
  "toute-la-vie": InfinityIcon,
  "ia-formation-pro": Briefcase,
  "ia-metiers-manuels": Hammer,
  "ia-sup": Landmark,
  "ia-sante": Stethoscope,
  "ia-relations": Users,
  "ia-couple": Heart,
  "ia-amitie": Handshake,
  "ia-transmission": History,
  "ia-armement": Crosshair,
  "ia-proliferation": FlaskConical,
  "ia-ethique-combattant": Swords,
  "ia-dissuasion": Radio,
  "ia-recherche": Microscope,
  "ia-serendipite": Dices,
  "ia-integrite": ShieldCheck,
  "ia-privatisation": Lock,
};

export default function Echo() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const topics = t("echo.topics", { returnObjects: true }) || [];
  const [likes, setLikes] = useState({});

  useEffect(() => {
    const vid = getVoterId();
    api.get(`/echo/likes?voter_id=${encodeURIComponent(vid)}`).then(({ data }) => {
      const map = {};
      (data.likes || []).forEach((l) => { map[l.topic_id] = { likes: l.likes, liked: l.liked }; });
      setLikes(map);
    }).catch(() => {});
  }, []);

  const toggleLike = async (topicId) => {
    const vid = getVoterId();
    // optimistic
    setLikes((prev) => {
      const cur = prev[topicId] || { likes: 0, liked: false };
      return { ...prev, [topicId]: { likes: cur.likes + (cur.liked ? -1 : 1), liked: !cur.liked } };
    });
    try {
      const { data } = await api.post("/echo/like", { topic_id: topicId, voter_id: vid });
      setLikes((prev) => ({ ...prev, [topicId]: { likes: data.likes, liked: data.liked } }));
    } catch {
      // revert on failure
      setLikes((prev) => {
        const cur = prev[topicId] || { likes: 0, liked: false };
        return { ...prev, [topicId]: { likes: cur.likes + (cur.liked ? -1 : 1), liked: !cur.liked } };
      });
    }
  };

  const discuss = (topic) => {
    const prompt = t("echo.prompt", { title: topic.title, question: topic.question });
    localStorage.setItem("coccon_pending_prompt", prompt);
    navigate("/atelier");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="font-display text-xl sm:text-2xl tracking-tight whitespace-nowrap" data-testid="echo-brand"><CocoonMark withMind /></Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <LanguageSwitcher className="hidden sm:flex" />
            <Link to="/manifeste" className="text-sm text-foreground/60 hover:text-primary transition-colors">{t("nav.manifeste")}</Link>
            <Link to="/tarifs" className="text-sm text-foreground/60 hover:text-primary transition-colors">{t("nav.tarifs")}</Link>
            <Link to="/entrer" data-testid="echo-enter-btn" className="btn-frost-orange text-sm font-semibold px-4 sm:px-6 py-2 rounded-full">{t("nav.entrer")}</Link>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-36 pb-24">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
            className="text-primary/80 tracking-[0.25em] uppercase text-xs mb-5">{t("echo.eyebrow")}</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.1 }}
            className="font-display text-gradient-orange text-5xl sm:text-6xl tracking-tight leading-[1] mb-6">{t("echo.title")}</motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.25 }}
            className="text-foreground/60 leading-relaxed text-lg">{t("echo.subtitle")}</motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.3 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <CinematicVideo
            fade
            src="https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/ubqpiom7_gemini_generated_video_8aa2c992.mp4"
            testId="echo-cinematic-video"
            caption={t("echo.subtitle")}
          />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="echo-grid">
          {[...topics].sort((a, b) => (likes[b.id]?.likes || 0) - (likes[a.id]?.likes || 0)).map((topic, i) => {
            const Icon = ICONS[topic.id] || BrainCircuit;
            const isPopular = i === 0 && (likes[topic.id]?.likes || 0) > 0;
            return (
              <motion.article
                key={topic.id}
                layout
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: Math.min(i, 6) * 0.05 }}
                data-testid={`echo-card-${topic.id}`}
                className="glass frame-pulse rounded-2xl p-8 flex flex-col"
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <Icon className="text-primary" size={28} strokeWidth={1.5} />
                    {isPopular && (
                      <span data-testid="echo-popular-badge" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/15 text-primary text-xs font-semibold border border-primary/30">
                        <Flame size={12} fill="currentColor" /> {t("echo.popular")}
                      </span>
                    )}
                  </div>
                  <Link to={`/echo/${topic.id}`} data-testid={`echo-permalink-${topic.id}`} aria-label={t("echo.readMore")} className="text-foreground/30 hover:text-primary transition-colors">
                    <Share2 size={18} />
                  </Link>
                </div>
                <Link to={`/echo/${topic.id}`} className="block font-display text-2xl md:text-3xl mb-3 leading-snug hover:text-primary transition-colors">{topic.title}</Link>
                <p className="text-foreground/65 leading-relaxed mb-5 flex-1">{topic.teaser}</p>
                <p className="font-display italic text-lg text-primary/90 mb-6">{topic.question}</p>
                <div className="flex items-center gap-3 self-start">
                  <button
                    data-testid={`echo-discuss-${topic.id}`}
                    onClick={() => discuss(topic)}
                    className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm"
                  >
                    <MessageCircle size={16} /> {t("echo.cta")}
                  </button>
                  <button
                    data-testid={`echo-like-${topic.id}`}
                    onClick={() => toggleLike(topic.id)}
                    aria-label={t("echo.likeAria")}
                    className={`inline-flex items-center gap-2 px-4 py-3 rounded-full text-sm border transition-colors ${
                      likes[topic.id]?.liked
                        ? "bg-primary/15 text-primary border-primary/40"
                        : "text-foreground/50 border-white/10 hover:text-primary hover:border-primary/30"
                    }`}
                  >
                    <Heart size={16} fill={likes[topic.id]?.liked ? "currentColor" : "none"} />
                    <span className="tabular-nums">{likes[topic.id]?.likes || 0}</span>
                  </button>
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4 text-sm text-foreground/40">
          <Link to="/" className="font-display text-lg"><CocoonMark letterClassName="text-foreground/60" withMind /></Link>
          <span>{t("landing.footerTagline")}</span>
        </div>
      </footer>
    </div>
  );
}
